import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  FileText,
  User,
  Upload,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  AlertCircle,
  Info,
} from 'lucide-react';
import { Card } from '@components/common/Card';
import { Badge } from '@components/common/Badge';
import { Button } from '@components/common/Button';
import { Input } from '@components/forms/Input';
import { Select } from '@components/forms/Select';
import { PageHeader } from '@components/common/PageHeader';
import { LICENSE_CATEGORIES, LICENSE_CATEGORY_LABELS, LICENSE_TYPES } from '@utils/constants';
import { getLicenseRequirements, type LicenseRequirement } from '@utils/licenseRequirements';
import { getCurrentApplicant } from '@mocks/applicants';

type ApplicationType = 'NEW' | 'RENEWAL';
type LicenseCategory = keyof typeof LICENSE_CATEGORIES;

interface FormData {
  [key: string]: any;
}

export const ApplicationWizard: React.FC = () => {
  const navigate = useNavigate();
  const applicant = getCurrentApplicant();

  // Step 0: Application Type & License Selection
  const [applicationType, setApplicationType] = useState<ApplicationType | null>(null);
  const [licenseCategory, setLicenseCategory] = useState<LicenseCategory | null>(null);
  const [requirements, setRequirements] = useState<LicenseRequirement | null>(null);

  // Wizard state
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<FormData>({});
  const [uploadedDocuments, setUploadedDocuments] = useState<Record<string, File>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm();

  // Load requirements when type and category are selected
  useEffect(() => {
    if (applicationType && licenseCategory) {
      const reqs = getLicenseRequirements(LICENSE_CATEGORIES[licenseCategory], applicationType);
      setRequirements(reqs);
    }
  }, [applicationType, licenseCategory]);

  // Calculate total steps: 1 (selection) + requirement sections + 1 (documents) + 1 (review)
  const totalSteps = requirements
    ? 1 + requirements.requiredFields.length + 1 + 1
    : 1;

  const handleNext = () => {
    // Validate current step before proceeding
    if (currentStep === 0) {
      if (!applicationType || !licenseCategory) {
        toast.error('Please select application type and license category');
        return;
      }
    }

    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleFieldChange = (fieldId: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const handleFileUpload = (documentId: string, file: File) => {
    setUploadedDocuments(prev => ({
      ...prev,
      [documentId]: file,
    }));
    toast.success(`Document "${file.name}" uploaded successfully`);
  };

  const onSubmit = async () => {
    setIsSubmitting(true);

    // Check all required documents are uploaded
    const missingDocs = requirements?.requiredDocuments
      .filter(doc => doc.required && !uploadedDocuments[doc.id])
      .map(doc => doc.label);

    if (missingDocs && missingDocs.length > 0) {
      toast.error(`Missing required documents: ${missingDocs.join(', ')}`);
      setIsSubmitting(false);
      return;
    }

    // Mock API call
    await new Promise((resolve) => setTimeout(resolve, 2000));

    const applicationData = {
      applicant_id: applicant.id,
      application_type: applicationType,
      license_category: licenseCategory,
      form_data: formData,
      documents: Object.keys(uploadedDocuments),
      submitted_at: new Date().toISOString(),
    };

    console.log('✅ Mock: Application submitted', applicationData);

    toast.success('Application submitted successfully!', {
      duration: 5000,
      icon: '🎉',
    });

    setIsSubmitting(false);

    // Redirect to status page
    setTimeout(() => {
      navigate('/status');
    }, 1500);
  };

  // Render Step 0: Application Type & License Selection
  const renderSelectionStep = () => (
    <div className="space-y-8">
      {/* Alert - New License Applications Disabled */}
      <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-lg">
        <div className="flex">
          <div className="flex-shrink-0">
            <AlertCircle className="h-5 w-5 text-amber-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-amber-800">
              New License Applications Currently Unavailable
            </h3>
            <div className="mt-2 text-sm text-amber-700">
              <p>
                We are not currently processing new license applications. Only license renewals are available at this time.
                Please check back later or contact our support team for more information.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Application Type Selection */}
      <Card>
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Application Type <span className="text-red-500">*</span>
          </h3>
          <p className="text-sm text-gray-600">
            Are you applying for a new license or renewing an existing one?
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => setApplicationType('NEW')}
            disabled={true}
            className={`p-6 border-2 rounded-lg transition-all opacity-50 cursor-not-allowed bg-gray-100 border-gray-300`}
          >
            <div className="flex items-center gap-3 mb-2">
              <FileText className="h-6 w-6 text-gray-400" />
              <h4 className="text-lg font-semibold text-gray-500">New License</h4>
            </div>
            <p className="text-sm text-gray-500 text-left">
              Currently unavailable
            </p>
          </button>

          <button
            type="button"
            onClick={() => setApplicationType('RENEWAL')}
            className={`p-6 border-2 rounded-lg transition-all ${applicationType === 'RENEWAL'
              ? 'border-ncaa-primary bg-blue-50 shadow-md'
              : 'border-gray-200 hover:border-gray-300'
              }`}
          >
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle className={`h-6 w-6 ${applicationType === 'RENEWAL' ? 'text-ncaa-primary' : 'text-gray-400'}`} />
              <h4 className="text-lg font-semibold text-gray-900">Renewal/Existing License</h4>
            </div>
            <p className="text-sm text-gray-600 text-left">
              Renew your existing aviation license
            </p>
          </button>
        </div>
      </Card>

      {/* License Category Selection */}
      {applicationType && (
        <Card>
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              License Category <span className="text-red-500">*</span>
            </h3>
            <p className="text-sm text-gray-600">
              Select the type of aviation license you're {applicationType === 'NEW' ? 'applying for' : 'renewing'}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3">
            {Object.entries(LICENSE_CATEGORY_LABELS).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setLicenseCategory(key as LicenseCategory)}
                className={`p-4 border-2 rounded-lg text-left transition-all ${licenseCategory === key
                  ? 'border-ncaa-primary bg-blue-50 shadow-sm'
                  : 'border-gray-200 hover:border-gray-300'
                  }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900">{label}</span>
                  {licenseCategory === key && (
                    <Badge variant="primary" size="sm">Selected</Badge>
                  )}
                </div>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Information Alert */}
      {applicationType && licenseCategory && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-blue-900 mb-1">Ready to proceed</h4>
              <p className="text-sm text-blue-800">
                You're applying for: <strong>{LICENSE_CATEGORY_LABELS[licenseCategory]}</strong> ({applicationType === 'NEW' ? 'New License' : 'Renewal'})
              </p>
              <p className="text-xs text-blue-700 mt-2">
                Click "Next" to continue with the application form
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Render dynamic form fields based on requirements
  const renderFormFields = (sectionIndex: number) => {
    if (!requirements) return null;

    const section = requirements.requiredFields[sectionIndex];

    return (
      <Card>
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{section.section}</h3>
          {applicationType === 'RENEWAL' && sectionIndex === 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-yellow-800">
                <strong>Renewal Application:</strong> Please provide your existing license details
              </p>
            </div>
          )}
        </div>

        <div className="space-y-4">
          {section.fields.map((field) => {
            const value = formData[field.id] || '';

            switch (field.type) {
              case 'select':
                return (
                  <div key={field.id}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <select
                      value={value}
                      onChange={(e) => handleFieldChange(field.id, e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ncaa-primary focus:border-transparent"
                      required={field.required}
                    >
                      <option value="">Select {field.label}</option>
                      {field.options?.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {field.helpText && (
                      <p className="mt-1 text-xs text-gray-500">{field.helpText}</p>
                    )}
                  </div>
                );

              case 'textarea':
                return (
                  <div key={field.id}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <textarea
                      value={value}
                      onChange={(e) => handleFieldChange(field.id, e.target.value)}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ncaa-primary focus:border-transparent"
                      placeholder={`Enter ${field.label.toLowerCase()}`}
                      required={field.required}
                    />
                    {field.helpText && (
                      <p className="mt-1 text-xs text-gray-500">{field.helpText}</p>
                    )}
                  </div>
                );

              case 'number':
                return (
                  <div key={field.id}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <input
                      type="number"
                      value={value}
                      onChange={(e) => handleFieldChange(field.id, e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ncaa-primary focus:border-transparent"
                      placeholder={`Enter ${field.label.toLowerCase()}`}
                      required={field.required}
                    />
                    {field.helpText && (
                      <p className="mt-1 text-xs text-gray-500">{field.helpText}</p>
                    )}
                  </div>
                );

              case 'date':
                return (
                  <div key={field.id}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <input
                      type="date"
                      value={value}
                      onChange={(e) => handleFieldChange(field.id, e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ncaa-primary focus:border-transparent"
                      required={field.required}
                    />
                    {field.helpText && (
                      <p className="mt-1 text-xs text-gray-500">{field.helpText}</p>
                    )}
                  </div>
                );

              default: // text
                return (
                  <div key={field.id}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => handleFieldChange(field.id, e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ncaa-primary focus:border-transparent"
                      placeholder={`Enter ${field.label.toLowerCase()}`}
                      required={field.required}
                    />
                    {field.helpText && (
                      <p className="mt-1 text-xs text-gray-500">{field.helpText}</p>
                    )}
                  </div>
                );
            }
          })}
        </div>
      </Card>
    );
  };

  // Render document upload step
  const renderDocumentStep = () => {
    if (!requirements) return null;

    return (
      <Card>
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Required Documents</h3>
          <p className="text-sm text-gray-600">
            Upload all required documents for your {applicationType === 'NEW' ? 'new license' : 'license renewal'} application
          </p>
        </div>

        <div className="space-y-4">
          {requirements.requiredDocuments.map((doc) => {
            const uploaded = uploadedDocuments[doc.id];

            return (
              <div key={doc.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-gray-900">
                      {doc.label}
                      {doc.required && <span className="text-red-500 ml-1">*</span>}
                    </h4>
                    {doc.helpText && (
                      <p className="text-xs text-gray-600 mt-1">{doc.helpText}</p>
                    )}
                  </div>
                  {uploaded && (
                    <Badge variant="success" size="sm">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Uploaded
                    </Badge>
                  )}
                </div>

                <div className="mt-3">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleFileUpload(doc.id, file);
                        }
                      }}
                      className="hidden"
                      accept={doc.id === 'existing_license' ? '.pdf' : '.pdf,.jpg,.jpeg,.png,.doc,.docx'}
                    />
                    <div className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-ncaa-primary transition-colors">
                      <Upload className="h-4 w-4 text-gray-500" />
                      <span className="text-sm text-gray-600">
                        {uploaded ? uploaded.name : 'Click to upload file'}
                      </span>
                    </div>
                  </label>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex gap-2">
            <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0" />
            <div className="text-sm text-yellow-800">
              <p className="font-medium mb-1">Document Requirements:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>All documents must be clear and legible</li>
                <li>Accepted formats: PDF, JPG, PNG, DOC, DOCX</li>
                <li>Existing license copy must be in PDF format only</li>
                <li>Maximum file size: 10MB per document</li>
                <li>Documents with * are required</li>
              </ul>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  // Render review and submit step
  const renderReviewStep = () => {
    if (!requirements) return null;

    const requiredDocsCount = requirements.requiredDocuments.filter(d => d.required).length;
    const uploadedRequiredDocsCount = requirements.requiredDocuments
      .filter(d => d.required && uploadedDocuments[d.id]).length;

    return (
      <div className="space-y-6">
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Application Summary</h3>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 pb-4 border-b border-gray-200">
              <div>
                <p className="text-sm text-gray-600">Application Type</p>
                <p className="font-semibold text-gray-900">
                  {applicationType === 'NEW' ? 'New License' : 'License Renewal'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">License Category</p>
                <p className="font-semibold text-gray-900">
                  {licenseCategory && LICENSE_CATEGORY_LABELS[licenseCategory]}
                </p>
              </div>
            </div>

            {requirements.requiredFields.map((section, idx) => (
              <div key={idx} className="pb-4 border-b border-gray-200">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">{section.section}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {section.fields.map(field => {
                    const value = formData[field.id];
                    if (!value) return null;

                    return (
                      <div key={field.id}>
                        <p className="text-xs text-gray-600">{field.label}</p>
                        <p className="text-sm text-gray-900">{value}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Documents</h4>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm text-gray-600">
                  {uploadedRequiredDocsCount} of {requiredDocsCount} required documents uploaded
                </span>
                {uploadedRequiredDocsCount === requiredDocsCount ? (
                  <Badge variant="success" size="sm">Complete</Badge>
                ) : (
                  <Badge variant="warning" size="sm">Incomplete</Badge>
                )}
              </div>
              <div className="space-y-1">
                {requirements.requiredDocuments.map(doc => (
                  uploadedDocuments[doc.id] && (
                    <div key={doc.id} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-gray-700">{doc.label}</span>
                    </div>
                  )
                ))}
              </div>
            </div>
          </div>
        </Card>

        <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              required
              className="w-5 h-5 mt-0.5 text-ncaa-primary border-gray-300 rounded focus:ring-ncaa-primary"
            />
            <div className="text-sm text-gray-700">
              <p className="font-medium mb-1">Declaration</p>
              <p>
                I declare that all information provided in this application is true and accurate to
                the best of my knowledge. I understand that providing false information may result
                in the rejection of my application and potential legal consequences.
              </p>
            </div>
          </label>
        </div>
      </div>
    );
  };

  // Render current step content
  const renderStepContent = () => {
    if (currentStep === 0) {
      return renderSelectionStep();
    }

    if (!requirements) return null;

    const formSectionCount = requirements.requiredFields.length;

    // Form field sections
    if (currentStep >= 1 && currentStep <= formSectionCount) {
      return renderFormFields(currentStep - 1);
    }

    // Document upload step
    if (currentStep === formSectionCount + 1) {
      return renderDocumentStep();
    }

    // Review step
    if (currentStep === formSectionCount + 2) {
      return renderReviewStep();
    }

    return null;
  };

  // Generate step labels for progress indicator
  const getStepLabel = (stepIndex: number): string => {
    if (stepIndex === 0) return 'Selection';
    if (!requirements) return '';

    const formSectionCount = requirements.requiredFields.length;

    if (stepIndex >= 1 && stepIndex <= formSectionCount) {
      return requirements.requiredFields[stepIndex - 1].section;
    }

    if (stepIndex === formSectionCount + 1) return 'Documents';
    if (stepIndex === formSectionCount + 2) return 'Review';

    return '';
  };

  const isLastStep = currentStep === totalSteps - 1;
  const canProceed = currentStep === 0 ? (applicationType && licenseCategory) : true;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        title="Apply for License"
        description={`Complete the application form to ${applicationType === 'RENEWAL' ? 'renew' : 'apply for'} your aviation license`}
      />

      {/* Progress Indicator */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium text-gray-700">
            Step {currentStep + 1} of {totalSteps}: {getStepLabel(currentStep)}
          </p>
          <p className="text-sm text-gray-500">
            {Math.round(((currentStep + 1) / totalSteps) * 100)}% Complete
          </p>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-ncaa-primary h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
          />
        </div>
      </div>

      {/* Step Content */}
      <div className="mb-8">
        {renderStepContent()}
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={handlePrevious}
          disabled={currentStep === 0}
          icon={<ArrowLeft className="h-4 w-4" />}
        >
          Previous
        </Button>

        {!isLastStep ? (
          <Button
            onClick={handleNext}
            disabled={!canProceed}
            icon={<ArrowRight className="h-4 w-4" />}
            iconPosition="right"
          >
            Next
          </Button>
        ) : (
          <Button
            onClick={onSubmit}
            disabled={isSubmitting}
            icon={<CheckCircle className="h-4 w-4" />}
            iconPosition="right"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Application'}
          </Button>
        )}
      </div>
    </div>
  );
};
